import React, { forwardRef, useEffect, useImperativeHandle, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from 'antd';
import { Circle, Layer, Line, Rect, Stage, Transformer } from 'react-konva';
import { useStore } from '../../store/store';
import { getAccentColor } from '../../utils/theme';
import { uuid } from '../../constants/constants';
import {
  captureStageThumbnail,
  getShapeComponent,
  getShadowCanvasMargin,
  LOCK_PROPORTIONS_SHAPES,
  subShapeTransformerAnchors,
  SUB_SHAPE_COLOR,
  ZONE_COLOR,
} from './shapeUtils';
import {
  collectLinks,
  computeLinkGeometry,
  findLink,
  getAnchor,
  getAnchors,
  hasLinkBetween,
  linkHandlePositions,
  mutateAddLink,
  mutateRetargetLink,
  nearestAnchor,
  subShapeAtPoint,
} from './shapeLinks';
import { ShapeComponent } from './Shape';
import ShapeLinkPanel from './ShapeLinkPanel';
import ShapePanelSections from './ShapePanelSections';
import ShapeTextOverlay, { applyLinkLabelPosition, applySubShapeTextFrame } from './ShapeTextOverlay';
import { linkLabelPoint } from './shapeText';
import { FormControlsPortalContext } from '../../contexts/FormControlsPortalContext';

// Emprise supplémentaire en pixels écran (convertie en unités de la box plus
// bas, cf. zoneHandleRoom) réservée aux poignées de la zone : elle ne dépasse
// la box que côté bas/droit (seuls enabledAnchors du Transformer de zone),
// donc pas besoin d'en ajouter ailleurs ni de décaler le contenu, qui reste
// ancré à (margin, margin).
const ZONE_HANDLE_ROOM = 20;

// Curseur de l'ancre de rotation du Transformer : Konva assigne cette chaîne
// telle quelle au CSS `cursor` du conteneur du Stage (cf. Konva.Transformer
// #rotateAnchorCursor, défaut 'crosshair') — une icône FA (fa-solid
// fa-rotate-right, tracé recopié comme dans ShapesStarIcon/ShapesStarEditIcon)
// encodée en data URI plutôt que le crosshair générique par défaut.
const ROTATE_CURSOR_SVG = encodeURIComponent(
  "<svg xmlns='http://www.w3.org/2000/svg' width='28' height='28' viewBox='0 0 512 512'>" +
  "<path fill='black' d='M463.5 224H472c13.3 0 24-10.7 24-24V72c0-9.7-5.8-18.5-14.8-22.2s-19.3-1.7-26.2 5.2L413.4 96.6c-87.6-86.5-228.7-86.2-315.8 1c-87.5 87.5-87.5 229.3 0 316.8s229.3 87.5 316.8 0c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0c-62.5 62.5-163.8 62.5-226.3 0s-62.5-163.8 0-226.3c62.2-62.2 162.7-62.5 225.3-1L327 183c-6.9 6.9-8.9 17.2-5.2 26.2s12.5 14.8 22.2 14.8H463.5z'/>" +
  '</svg>'
);
const ROTATE_ANCHOR_CURSOR = `url("data:image/svg+xml,${ROTATE_CURSOR_SVG}") 14 14, pointer`;

const selector = (boxId) => ({ updateBox }) => ({ updateBox });

// Sentinelle de sélection pour la zone d'affichage elle-même (box.width/
// height, contre laquelle les sous-formes sont rognées) — distincte de
// `null` (qui retombe sur la première sous-forme, cf. activeSubShapeId) et de
// tout id réel de sous-forme.
const ZONE_SELECTION = '__zone__';
// Même principe pour un lien sélectionné (cf. selectedLink, qui dit lequel).
const LINK_SELECTION = '__link__';

// Poignées de création de lien, en pixels écran (divisés par le zoom) : rayon,
// et écart au bord de la forme — au-delà des ancres du Transformer, posées au
// milieu des mêmes côtés.
const LINK_HANDLE_RADIUS = 6;
const LINK_HANDLE_OFFSET = 18;
// Ancres de la forme survolée pendant un tracé : rayon des pastilles, et
// distance (pixels écran) à laquelle une ancre s'attrape depuis l'extérieur.
const HOVER_ANCHOR_RADIUS = 4;
const HOVER_ANCHOR_TOLERANCE = 14;
// Cf. l'effet qui l'applique, près de textEditing.
const TEXT_TOOLBARS_Z_INDEX = 10110;

const endpointsOf = (geometry) => geometry && {
  from: geometry.points[0],
  to: geometry.points[geometry.points.length - 1],
};

// Position et taille courantes d'une sous-forme, lues sur son nœud Konva
// pendant un geste (glisser, redimensionner, tourner) : le store ne les reçoit
// qu'au relâché. Le Group pivote autour de son centre (cf. getShapeComponent).
const liveSubShape = (sub, node) => {
  if (!node) return sub;
  const width = sub.width * node.scaleX();
  const height = sub.height * node.scaleY();
  return { ...sub, x: node.x() - width / 2, y: node.y() - height / 2, width, height, rotation: node.rotation() };
};

// Repère d'accroche : vert (comme la zone d'affichage) quand il vient du
// canevas, rouge quand il vient d'une autre sous-forme.
const snapGuideColor = (source) => (source === 'canvas' ? ZONE_COLOR : '#ff4d4f');

// Même mécanique que le canvas de Shape.jsx (Transformer, glisser-déposer,
// guides d'accroche) mais sur un Stage plus grand mis à l'échelle pour
// remplir la fenêtre — reprend getShapeComponent tel quel (les coordonnées
// des sous-formes restent dans le référentiel de la box, c'est le Stage
// lui-même qui zoome, pas leurs coordonnées).
// `fullscreen` : rendu sans la chrome Modal (pas de fond assombri, pas de
// titre, pas de croix) — pour l'iframe "composer une forme" de la banque
// admin (cf. shapeScratchApp.jsx), qui EST déjà la seule chose affichée dans
// sa fenêtre ; une Modal par-dessus n'y aurait aucun sens. `sidePanelExtra`
// s'affiche au-dessus du panneau de réglages, dans les deux cas (ex. le
// bouton "Terminer" de cette même iframe).
const ShapeFullscreenEditor = forwardRef(({ box, open, onClose, fullscreen = false, sidePanelExtra = null }, ref) => {
  const { t } = useTranslation();
  const { updateBox } = useStore(selector(box.id));
  const subShapes = box.content?.shapes ?? [];

  const [selectedSubShapeId, setSelectedSubShapeId] = useState(subShapes[0]?.id ?? null);
  // Sélection multiple (Ctrl / Cmd + clic) : plusieurs formes, seulement quand
  // elles sont deux ou plus — sinon on retombe sur la sélection simple ci-dessus.
  const [multiSelectedIds, setMultiSelectedIds] = useState([]);
  const [selectedLink, setSelectedLink] = useState(null);
  const isZoneSelected = selectedSubShapeId === ZONE_SELECTION;
  // Un lien supprimé (ou dont une extrémité a disparu) retombe sur la sélection
  // par défaut, comme une sous-forme supprimée.
  const isLinkSelected = selectedSubShapeId === LINK_SELECTION
    && !!selectedLink && !!findLink(subShapes, selectedLink.ownerId, selectedLink.linkId);
  const activeSubShapeId = isZoneSelected || isLinkSelected
    ? null
    : (subShapes.some((s) => s.id === selectedSubShapeId) ? selectedSubShapeId : subShapes[0]?.id);
  const activeSubShape = subShapes.find((s) => s.id === activeSubShapeId);
  const selectedIds = multiSelectedIds.filter((id) => subShapes.some((s) => s.id === id));
  const isMultiSelection = !isZoneSelected && !isLinkSelected && selectedIds.length > 1;

  const subShapeNodesRef = useRef({});
  const transformerRef = useRef(null);
  const zoneNodeRef = useRef(null);
  const zoneTransformerRef = useRef(null);
  // Vignette de sauvegarde en bibliothèque (cf. Shape.jsx#handleGenerateThumbnail
  // — même principe, sur le Stage zoomé de la modale).
  const stageRef = useRef(null);
  // Le Stage remplit tout le conteneur (cf. stageWidth/Height plus bas) : sans
  // le cropRect, la vignette engloberait toute la marge transparente autour
  // du contenu au lieu de ne garder que la zone utile (layerOffsetX/Y,
  // stageContentWidth/Height * scale).
  const handleGenerateThumbnail = () => captureStageThumbnail(stageRef.current, transformerRef.current, {
    x: layerOffsetX,
    y: layerOffsetY,
    width: stageContentWidth * scale,
    height: stageContentHeight * scale,
  });
  const registerSubShapeNode = (subShapeId, node) => {
    if (node) {
      subShapeNodesRef.current[subShapeId] = node;
      // Le Transformer prend le getClientRect du Group, soit l'union de ses
      // enfants : pour une étoile, Konva renvoie le carré 2R×2R du cercle
      // circonscrit, plus grand que la vraie boîte (sub.width/height, portée
      // par les attributs width/height du Group). Le cadre (et donc le pivot
      // de rotation, cf. offset du Group) doit être cette boîte-là.
      const groupGetClientRect = Object.getPrototypeOf(node).getClientRect;
      node.getClientRect = (config) => (config?.skipTransform
        ? { x: 0, y: 0, width: node.width(), height: node.height() }
        : groupGetClientRect.call(node, config));
    } else delete subShapeNodesRef.current[subShapeId];
  };

  // Repères d'accroche : mis à jour directement sur leurs nœuds pendant le
  // glissement, sans état React — un rendu par pixel déplacé redessinerait tout
  // l'éditeur et remettrait les liens à leur position enregistrée en plein geste.
  const guideXRef = useRef(null);
  const guideYRef = useRef(null);
  const updateSnapGuides = ({ guideX, guideY, guideXSource, guideYSource }) => {
    const apply = (node, value, source, points) => {
      if (!node) return;
      node.visible(value != null);
      if (value != null) {
        node.points(points(value));
        node.stroke(snapGuideColor(source));
      }
    };
    apply(guideXRef.current, guideX, guideXSource, (x) => [margin + x, 0, margin + x, stageContentHeight]);
    apply(guideYRef.current, guideY, guideYSource, (y) => [0, margin + y, stageContentWidth, margin + y]);
    guideXRef.current?.getLayer()?.batchDraw();
  };
  const hideSnapGuides = () => updateSnapGuides({ guideX: null, guideY: null });

  // Liens et poignées suivent la forme pendant un geste : au plus un calcul par
  // image, lu sur les nœuds Konva eux-mêmes (cf. liveSubShape). Tous les liens
  // sont recalculés : il y en a peu, et un déplacement de groupe en bouge
  // plusieurs sans que chaque nœud ne prévienne.
  const subShapesRef = useRef(subShapes);
  subShapesRef.current = subShapes;
  const activeSubShapeIdRef = useRef(activeSubShapeId);
  activeSubShapeIdRef.current = activeSubShapeId;
  const linkHandleNodesRef = useRef({});
  const linkEndpointNodesRef = useRef({});
  // Cadres des textes (sous-formes, libellés de liens) posés par-dessus le
  // canvas, déplacés avec lui pendant un geste.
  const textNodesRef = useRef({});
  const registerTextNode = (key, element) => {
    if (element) textNodesRef.current[key] = element;
    else delete textNodesRef.current[key];
  };
  const selectedLinkIdRef = useRef(null);
  const liveFrameRef = useRef(null);
  const liveSubShapeById = (id) => {
    const sub = subShapesRef.current.find((s) => s.id === id);
    return sub && liveSubShape(sub, subShapeNodesRef.current[id]);
  };
  const refreshLinksLive = () => {
    if (liveFrameRef.current) return;
    liveFrameRef.current = requestAnimationFrame(() => {
      liveFrameRef.current = null;
      const stage = stageRef.current;
      if (!stage) return;
      stage.find('.shapeLink').forEach((node) => {
        const from = liveSubShapeById(node.getAttr('fromId'));
        const to = liveSubShapeById(node.getAttr('toId'));
        if (!from || !to) return;
        const geometry = computeLinkGeometry(node.getAttr('linkType'), from, to, {
          fromAnchor: node.getAttr('fromAnchor'),
          toAnchor: node.getAttr('toAnchor'),
        });
        node.setAttr('geometry', geometry);
        const label = textNodesRef.current[`link:${node.getAttr('linkId')}`];
        if (label) applyLinkLabelPosition(label, linkLabelPoint(geometry), margin);
        const ends = node.getAttr('linkId') === selectedLinkIdRef.current && endpointsOf(geometry);
        if (ends) {
          ['from', 'to'].forEach((end) => linkEndpointNodesRef.current[end]?.position({
            x: margin + ends[end][0],
            y: margin + ends[end][1],
          }));
        }
      });
      subShapesRef.current.forEach((sub) => {
        const frame = textNodesRef.current[`sub:${sub.id}`];
        if (frame) applySubShapeTextFrame(frame, liveSubShapeById(sub.id), margin);
      });
      const active = liveSubShapeById(activeSubShapeIdRef.current);
      if (active) {
        linkHandlePositions(active, LINK_HANDLE_OFFSET / scale).forEach(({ anchor, x, y }) => {
          linkHandleNodesRef.current[anchor]?.position({ x: margin + x, y: margin + y });
        });
      }
      stage.batchDraw();
    });
  };
  useEffect(() => () => cancelAnimationFrame(liveFrameRef.current), []);

  // Expose la génération de vignette au parent (cf. shapeScratchApp.jsx) :
  // hors Modal, le bouton "Terminer" qui en a besoin vit dans sidePanelExtra,
  // un nœud construit par l'appelant — pas un enfant capable de partager l'état
  // interne de ce composant autrement que par cette ref.
  useImperativeHandle(ref, () => ({ generateThumbnail: handleGenerateThumbnail }));

  useEffect(() => {
    const transformer = transformerRef.current;
    if (!transformer) return;
    const ids = isMultiSelection ? selectedIds : [activeSubShapeId];
    transformer.nodes(ids.map((id) => subShapeNodesRef.current[id]).filter(Boolean));
    transformer.getLayer()?.batchDraw();
    // selectedIds : nouvelle référence à chaque rendu, sa clé stable suffit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSubShapeId, activeSubShape, isMultiSelection, selectedIds.join(',')]);

  useEffect(() => {
    const transformer = zoneTransformerRef.current;
    if (!transformer) return;
    transformer.nodes(isZoneSelected && zoneNodeRef.current ? [zoneNodeRef.current] : []);
    transformer.getLayer()?.batchDraw();
  }, [isZoneSelected]);

  const containerRef = useRef(null);
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });
  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el || !open) return;
    const observer = new ResizeObserver(([entry]) => {
      setContainerSize({ width: entry.contentRect.width, height: entry.contentRect.height });
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [open]);

  const margin = Math.max(0, ...subShapes.map((s) => getShadowCanvasMargin(s.shadow)));
  const baseContentWidth = box.width + margin * 2;
  const baseContentHeight = box.height + margin * 2;
  // Konva.Transformer garde ses ancres à taille écran constante quel que soit
  // le zoom du Stage (cf. zoneAnchorStyleFunc) : ZONE_HANDLE_ROOM (des pixels
  // écran, la moitié de la plus grande pilule + marge) doit donc être converti
  // en unités de la box en divisant par le zoom courant — approximé ici par
  // le zoom qu'on obtiendrait sans cette marge, pour éviter une dépendance
  // circulaire avec `scale` lui-même.
  const prelimScale = containerSize.width > 0 && baseContentWidth > 0 && baseContentHeight > 0
    ? Math.min(containerSize.width / baseContentWidth, containerSize.height / baseContentHeight, 4)
    : 1;
  const zoneHandleRoom = ZONE_HANDLE_ROOM / Math.max(prelimScale, 0.5);
  const stageContentWidth = baseContentWidth + zoneHandleRoom;
  const stageContentHeight = baseContentHeight + zoneHandleRoom;
  // Zoome pour remplir l'espace disponible sans jamais réduire une petite
  // forme à une vignette illisible ni l'agrandir au point de pixeliser —
  // 1x reste la référence, [0.5, 4] borne l'écart dans les deux sens.
  const fitScale = containerSize.width > 0 && stageContentWidth > 0 && stageContentHeight > 0
    ? Math.min(
      containerSize.width / stageContentWidth,
      containerSize.height / stageContentHeight,
      4
    )
    : 1;
  const scale = Math.max(fitScale, 0.5);
  // Le Stage remplit tout le conteneur disponible (pas seulement le contenu) :
  // un drag qui agrandit la zone au-delà de sa taille « au repos » (bordure,
  // poignées, cf. handleTransformZone) reste ainsi visible tant qu'il ne
  // dépasse pas le conteneur — bien plus généreux que la taille du contenu
  // lui-même, qui ne borne plus le canvas. Le contenu, inchangé, est recentré
  // dans cet espace via le décalage du Layer ci-dessous (à la place du
  // centrage flexbox qu'on perdrait sinon en agrandissant le Stage).
  const stageWidth = containerSize.width > 0 ? containerSize.width : stageContentWidth * scale;
  const stageHeight = containerSize.height > 0 ? containerSize.height : stageContentHeight * scale;
  const layerOffsetX = (stageWidth - stageContentWidth * scale) / 2;
  const layerOffsetY = (stageHeight - stageContentHeight * scale) / 2;

  // Un clic dans le vide (damier, ou intérieur de la zone — sans fill, il
  // laisse passer les clics) sélectionne la zone d'affichage ; un clic sur une
  // sous-forme ou une poignée a pour cible ce nœud, pas le Stage lui-même.
  const selectZone = () => {
    setMultiSelectedIds([]);
    setSelectedSubShapeId(ZONE_SELECTION);
  };
  const handleSelectLink = (ownerId, linkId) => {
    setMultiSelectedIds([]);
    setSelectedLink({ ownerId, linkId });
    setSelectedSubShapeId(LINK_SELECTION);
  };

  // Texte en cours de saisie : celui d'une sous-forme (ou d'un bloc texte) ou
  // le libellé d'un lien, ouvert d'un double-clic. Un appui ailleurs sur le
  // canvas, ou Échap, le referme — le texte s'enregistre alors (cf.
  // BoxDraftText). Ses réglages de mise en forme vont dans le panneau de la
  // modale (textPanelHost), pas dans le panel de droite qu'elle recouvre.
  const [textEditing, setTextEditing] = useState(null);
  const [textPanelHost, setTextPanelHost] = useState(null);
  const handleEditSubShapeText = (subShapeId) => {
    setMultiSelectedIds([]);
    setSelectedSubShapeId(subShapeId);
    setTextEditing({ type: 'sub', subId: subShapeId });
  };
  const handleEditLinkText = (ownerId, linkId) => {
    handleSelectLink(ownerId, linkId);
    setTextEditing({ type: 'link', ownerId, linkId });
  };
  // La barre flottante et le menu de variables du texte (cf. BoxDraftText)
  // vivent dans #SelectionVariablePortal, à 9998-9999 : sous cette modale, que
  // antd pose à zIndexPopupBase (10100, cf. Layout.jsx). Le temps de la saisie,
  // leur hôte forme un contexte d'empilement juste au-dessus d'elle — et sous
  // les listes antd ouvertes depuis la barre (police, taille : 10150) comme
  // sous les messages (10200, cf. index.less). Boîte fixe de taille nulle en
  // haut à gauche de la fenêtre : la barre (en `absolute`) et le menu (en
  // `fixed`) gardent leurs coordonnées d'écran, et l'hôte ne capte aucun clic.
  const isEditingText = !!textEditing;
  useEffect(() => {
    if (!isEditingText) return undefined;
    const host = document.getElementById('SelectionVariablePortal');
    if (!host) return undefined;
    const previous = host.getAttribute('style');
    Object.assign(host.style, {
      position: 'fixed',
      top: '0',
      left: '0',
      width: '0',
      height: '0',
      zIndex: String(TEXT_TOOLBARS_Z_INDEX),
    });
    return () => {
      if (previous == null) host.removeAttribute('style');
      else host.setAttribute('style', previous);
      // La sélection du texte refermé resterait surlignée sur sa version en
      // lecture seule.
      const selection = window.getSelection();
      if (selection?.anchorNode && containerRef.current?.contains(selection.anchorNode)) {
        selection.removeAllRanges();
      }
    };
  }, [isEditingText]);

  const stopTextEditingOnEscape = (e) => {
    if (!textEditing || e.key !== 'Escape') return;
    // Sans ça, Échap fermerait aussi la modale.
    e.stopPropagation();
    setTextEditing(null);
  };
  const selectZoneOnEmptyClick = (e) => {
    if (e.target !== e.target.getStage() || e.evt?.ctrlKey || e.evt?.metaKey) return;
    // Relâcher un tracé de lien dans le vide déclenche aussi un clic sur le
    // Stage : ce n'est pas une demande de sélection de la zone.
    if (linkDraftEndedRef.current) return;
    selectZone();
  };

  // Tracé d'un lien, en création (`create` : depuis une poignée d'ancre de la
  // forme active) ou en raccrochage d'une extrémité du lien sélectionné
  // (`retarget`). Survoler une forme affiche ses ancres et désigne la plus
  // proche, où le lien s'accroche au relâché. Le tracé provisoire suit le
  // pointeur sans état React ; seul le changement de forme/ancre survolée en
  // passe par un.
  const [linkDraft, setLinkDraft] = useState(null);
  const [linkHover, setLinkHover] = useState(null);
  const linkDraftEndedRef = useRef(false);
  const draftLineRef = useRef(null);
  const layerRef = useRef(null);

  const selectedLinkEntry = isLinkSelected
    ? collectLinks(subShapes).find(({ link }) => link.id === selectedLink.linkId)
    : null;
  const selectedLinkEnds = selectedLinkEntry && endpointsOf(computeLinkGeometry(
    selectedLinkEntry.link.type,
    selectedLinkEntry.from,
    selectedLinkEntry.to,
    selectedLinkEntry.link
  ));
  selectedLinkIdRef.current = selectedLinkEntry?.link.id ?? null;

  const startLinkDraft = (draft) => (e) => {
    e.cancelBubble = true;
    setLinkDraft(draft);
    setLinkHover(null);
  };
  const endLinkDraft = () => {
    setLinkDraft(null);
    setLinkHover(null);
    linkDraftEndedRef.current = true;
    setTimeout(() => {
      linkDraftEndedRef.current = false;
    });
  };
  // Point fixe du tracé : l'ancre de départ en création, l'autre extrémité du
  // lien en raccrochage. `excludeId` : la forme qu'on ne peut pas viser (un lien
  // ne relie pas une forme à elle-même).
  const draftFixedPoint = () => {
    if (linkDraft?.mode === 'create') {
      const from = subShapes.find((s) => s.id === linkDraft.fromId);
      return from && getAnchor(from, linkDraft.fromAnchor)?.point;
    }
    if (!selectedLinkEnds) return null;
    return linkDraft?.end === 'to' ? selectedLinkEnds.from : selectedLinkEnds.to;
  };
  const draftExcludedId = () => {
    if (linkDraft?.mode === 'create') return linkDraft.fromId;
    if (!selectedLinkEntry) return null;
    return linkDraft?.end === 'to' ? selectedLinkEntry.from.id : selectedLinkEntry.to.id;
  };
  const handleStagePointerMove = () => {
    const pointer = layerRef.current?.getRelativePointerPosition();
    const fixed = draftFixedPoint();
    if (!pointer || !fixed || !draftLineRef.current) return;
    const point = [pointer.x - margin, pointer.y - margin];
    const target = subShapeAtPoint(subShapes, point, {
      tolerance: HOVER_ANCHOR_TOLERANCE / scale,
      excludeId: draftExcludedId(),
    });
    const anchor = target && nearestAnchor(target, point);
    if (target?.id !== linkHover?.subId || anchor?.anchor !== linkHover?.anchor) {
      setLinkHover(target ? { subId: target.id, anchor: anchor.anchor } : null);
    }
    const end = anchor ? anchor.point : point;
    draftLineRef.current.points([margin + fixed[0], margin + fixed[1], margin + end[0], margin + end[1]]);
    draftLineRef.current.getLayer()?.batchDraw();
  };
  const handleStagePointerUp = () => {
    if (!linkDraft) return;
    const draft = linkDraft;
    const hover = linkHover;
    endLinkDraft();
    if (!hover) return;
    if (draft.mode === 'create') {
      if (hasLinkBetween(subShapes, draft.fromId, hover.subId)) return;
      const linkId = uuid();
      updateBox(box.id, (boxDraft) => {
        mutateAddLink(boxDraft, draft.fromId, hover.subId, linkId, { fromAnchor: draft.fromAnchor, toAnchor: hover.anchor });
      });
      handleSelectLink(draft.fromId, linkId);
      return;
    }
    // Rejoue le raccrochage sur une copie pour connaître la forme qui portera
    // le lien (elle change si on déplace son départ) avant de l'appliquer.
    const preview = { content: { shapes: JSON.parse(JSON.stringify(subShapes)) } };
    const ownerId = mutateRetargetLink(preview, selectedLink.ownerId, selectedLink.linkId, draft.end, hover.subId, hover.anchor);
    if (!ownerId) return;
    updateBox(box.id, (boxDraft) => {
      mutateRetargetLink(boxDraft, selectedLink.ownerId, selectedLink.linkId, draft.end, hover.subId, hover.anchor);
    });
    setSelectedLink({ ownerId, linkId: selectedLink.linkId });
  };
  // Relâché hors du canevas, ou Échap : on abandonne. Échap est intercepté avant
  // la Modal, qui se fermerait sinon avec.
  useEffect(() => {
    if (!linkDraft) return undefined;
    const cancelOnEscape = (e) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      endLinkDraft();
    };
    window.addEventListener('keydown', cancelOnEscape, true);
    window.addEventListener('pointerup', endLinkDraft);
    return () => {
      window.removeEventListener('keydown', cancelOnEscape, true);
      window.removeEventListener('pointerup', endLinkDraft);
    };
  }, [linkDraft]);
  const hoveredSubShape = linkDraft && linkHover && subShapes.find((s) => s.id === linkHover.subId);
  const setStageCursor = (cursor) => (e) => {
    const container = e.target.getStage()?.container();
    if (container) container.style.cursor = cursor;
  };

  // Clic sur une sous-forme : sélection simple ; avec Ctrl (Windows) ou
  // Cmd (Mac), elle s'ajoute à la sélection ou en est retirée.
  const handleSelectSubShape = (subShapeId, event) => {
    if (!(event?.ctrlKey || event?.metaKey)) {
      setMultiSelectedIds([]);
      setSelectedSubShapeId(subShapeId);
      return;
    }
    const current = isZoneSelected || isLinkSelected ? [] : (isMultiSelection ? selectedIds : [activeSubShapeId]);
    const next = current.includes(subShapeId) ? current.filter((id) => id !== subShapeId) : [...current, subShapeId];
    if (next.length === 0) return;
    setMultiSelectedIds(next.length > 1 ? next : []);
    setSelectedSubShapeId(next.includes(subShapeId) ? subShapeId : next[next.length - 1]);
  };

  // Sélection simple depuis le panneau (liste des formes).
  const handleSelectSubShapeFromPanel = (subShapeId) => {
    setMultiSelectedIds([]);
    setSelectedSubShapeId(subShapeId);
  };

  // Déplacement de plusieurs formes : le Transformer de Konva, auquel elles sont
  // toutes attachées, entraîne les autres nœuds avec celui qu'on tient (et leur
  // fait envoyer à chacun un `dragend`). Les positions sont enregistrées d'un
  // coup, une seule fois par geste.
  const groupDragCommitPendingRef = useRef(false);
  const handleDragSubShapesEnd = () => {
    if (groupDragCommitPendingRef.current) return;
    groupDragCommitPendingRef.current = true;
    queueMicrotask(() => {
      groupDragCommitPendingRef.current = false;
      updateBox(box.id, (draft) => {
        selectedIds.forEach((id) => {
          const sub = draft.content.shapes.find((s) => s.id === id);
          const node = subShapeNodesRef.current[id];
          if (!sub || !node) return;
          // Le groupe pivote autour de son centre : x/y du nœud est le centre.
          sub.x = node.x() - sub.width / 2;
          sub.y = node.y() - sub.height / 2;
        });
      });
      hideSnapGuides();
    });
  };

  const handleDragSubShapeEnd = (subShapeId, { x, y }) => {
    updateBox(box.id, (draft) => {
      const sub = draft.content.shapes.find((s) => s.id === subShapeId);
      if (sub) {
        sub.x = x;
        sub.y = y;
      }
    });
  };

  const handleTransformSubShapeEnd = (subShapeId, { x, y, width, height, rotation }) => {
    updateBox(box.id, (draft) => {
      const sub = draft.content.shapes.find((s) => s.id === subShapeId);
      if (sub) {
        sub.x = x;
        sub.y = y;
        sub.width = width;
        sub.height = height;
        sub.rotation = rotation;
      }
    });
  };

  // Redimensionne la zone d'affichage (box.width/height, contre laquelle les
  // sous-formes sont rognées — cf. getShapeComponent) sans toucher aux
  // sous-formes elles-mêmes : contrairement au champ Largeur/Hauteur du
  // panneau (qui les redimensionne proportionnellement, cf.
  // handleChangeContainerSize côté ShapeComponent), ce geste ne fait
  // qu'agrandir/rétrécir le cadre de recadrage — une sous-forme peut ainsi
  // apparaître/disparaître selon qu'elle rentre ou non dans la nouvelle zone,
  // sans jamais changer de taille ou de position. Seuls les coins/côtés
  // bas-droit sont activés (cf. enabledAnchors du Transformer) : la zone n'a
  // pas de position propre, l'agrandir depuis le haut/la gauche demanderait
  // de décaler toutes les sous-formes en compensation pour ne pas les voir
  // bouger visuellement — hors scope ici.
  // Aperçu en direct pendant le drag (pas seulement au relâcher) : le Group
  // de rognage (cf. name='compoundClipGroup' dans getShapeComponent) est mis
  // à jour impérativement, comme le fait déjà Resizer.jsx pour les box
  // classiques — un rendu React ne se déclenche qu'à `onTransformEnd`
  // (nouvelles box.width/height dans le store), donc sans ceci, seul le
  // contour en pointillé bougerait pendant le geste.
  const handleTransformZone = (e) => {
    const node = e.target;
    const clipGroup = node.getStage()?.findOne('.compoundClipGroup');
    if (!clipGroup) return;
    clipGroup.clipWidth(Math.max(20, box.width * node.scaleX()));
    clipGroup.clipHeight(Math.max(20, box.height * node.scaleY()));
    clipGroup.getLayer()?.batchDraw();
  };

  // Poignées de la zone façon Resizer.jsx (cf. resizeHandlersStyles) : pilule
  // blanche à bordure couleur d'accent, orientée selon le côté. Konva.Transformer
  // garde déjà ses ancres à taille écran constante quel que soit le zoom du
  // Stage (il compense en interne l'échelle des ancêtres) — ces tailles sont
  // donc de simples pixels écran, sans division par `scale`.
  const zoneAnchorStyleFunc = (anchor) => {
    anchor.stroke(getAccentColor());
    anchor.fill('white');
    anchor.strokeWidth(1);
    if (anchor.hasName('bottom-center')) {
      const w = 30;
      const h = 7;
      anchor.width(w);
      anchor.height(h);
      anchor.offsetX(w / 2);
      anchor.offsetY(h / 2);
      anchor.cornerRadius(h / 2);
    } else if (anchor.hasName('middle-right')) {
      const w = 7;
      const h = 30;
      anchor.width(w);
      anchor.height(h);
      anchor.offsetX(w / 2);
      anchor.offsetY(h / 2);
      anchor.cornerRadius(w / 2);
    } else if (anchor.hasName('bottom-right')) {
      const s = 14;
      anchor.width(s);
      anchor.height(s);
      anchor.offsetX(s / 2);
      anchor.offsetY(s / 2);
      anchor.cornerRadius(s / 2);
    }
  };

  const handleTransformZoneEnd = (e) => {
    const node = e.target;
    const width = Math.max(20, Math.round(box.width * node.scaleX()));
    const height = Math.max(20, Math.round(box.height * node.scaleY()));
    node.scaleX(1);
    node.scaleY(1);
    node.x(0);
    node.y(0);
    updateBox(box.id, (draft) => {
      draft.width = width;
      draft.height = height;
    });
  };

  const content = (
    <FormControlsPortalContext.Provider value={textPanelHost}>
    <div style={{ display: 'flex', gap: 16, height: fullscreen ? '100%' : '75vh' }}>
      <div
        ref={containerRef}
        style={{
          flex: 1,
          position: 'relative',
          overflow: 'hidden',
          background: 'repeating-conic-gradient(#f0f0f0 0% 25%, #fafafa 0% 50%) 50% / 16px 16px',
          borderRadius: 4,
        }}
      >
        {/* Le Stage remplit le conteneur (cf. stageWidth/Height) ; le contenu
            garde sa taille/zoom habituels (scaleX/scaleY) mais est recentré
            dedans via le décalage du Layer (layerOffsetX/Y) — le centrage
            n'est plus assuré par flexbox, qui recadrerait le Stage lui-même
            au lieu du contenu. */}
        <Stage
          ref={stageRef}
          width={stageWidth}
          height={stageHeight}
          onClick={selectZoneOnEmptyClick}
          onTap={selectZoneOnEmptyClick}
          onPointerMove={linkDraft ? handleStagePointerMove : undefined}
          onPointerUp={handleStagePointerUp}
          onPointerDown={textEditing ? () => setTextEditing(null) : undefined}
        >
          <Layer ref={layerRef} x={layerOffsetX} y={layerOffsetY} scaleX={scale} scaleY={scale}>
            {getShapeComponent(box, undefined, margin, {
              selectedSubShapeId: activeSubShapeId,
              selectedSubShapeIds: isMultiSelection ? selectedIds : undefined,
              onSelectSubShape: handleSelectSubShape,
              onDragSubShapesEnd: handleDragSubShapesEnd,
              onDragSubShapeEnd: handleDragSubShapeEnd,
              onDragSubShapeMove: updateSnapGuides,
              onTransformSubShapeEnd: handleTransformSubShapeEnd,
              registerSubShapeNode,
              selectedLinkId: isLinkSelected ? selectedLink.linkId : undefined,
              onSelectLink: handleSelectLink,
              onSubShapeLiveChange: refreshLinksLive,
              onEditSubShapeText: handleEditSubShapeText,
              onEditLinkText: handleEditLinkText,
            })}
            {/* Zone d'affichage (box.width/height) : le cadre en pointillé vert
                dit où les sous-formes seront rognées. Seule sa bordure répond
                au clic (hitStrokeWidth, pas de fill) — l'intérieur laisse
                passer les clics vers les sous-formes qu'il recouvre. */}
            <Rect
              ref={zoneNodeRef}
              name='zoneOverlay'
              x={margin}
              y={margin}
              width={box.width}
              height={box.height}
              stroke={ZONE_COLOR}
              strokeWidth={3 / scale}
              dash={[10 / scale, 8 / scale]}
              hitStrokeWidth={10 / scale}
              fillEnabled={false}
              onClick={selectZone}
              onTap={selectZone}
              onTransform={handleTransformZone}
              onTransformEnd={handleTransformZoneEnd}
            />
            {activeSubShapeId && (
              <Transformer
                ref={transformerRef}
                // Plusieurs formes : on ne peut que les déplacer.
                rotateEnabled={!isMultiSelection}
                resizeEnabled={!isMultiSelection}
                rotateAnchorCursor={ROTATE_ANCHOR_CURSOR}
                keepRatio={LOCK_PROPORTIONS_SHAPES.includes(activeSubShape?.shape)}
                enabledAnchors={subShapeTransformerAnchors(activeSubShape?.shape)}
                boundBoxFunc={(oldBox, newBox) => (newBox.width < 10 || newBox.height < 10 ? oldBox : newBox)}
              />
            )}
            {isZoneSelected && (
              <Transformer
                ref={zoneTransformerRef}
                name='zoneOverlay'
                rotateEnabled={false}
                enabledAnchors={['middle-right', 'bottom-center', 'bottom-right']}
                anchorStyleFunc={zoneAnchorStyleFunc}
                boundBoxFunc={(oldBox, newBox) => (newBox.width < 20 || newBox.height < 20 ? oldBox : newBox)}
              />
            )}
            {/* Poignées de création de lien de la forme active (une seule
                forme sélectionnée). */}
            {activeSubShape && !isMultiSelection && linkHandlePositions(activeSubShape, LINK_HANDLE_OFFSET / scale).map(({ anchor, x, y }) => (
              <Circle
                key={anchor}
                ref={(node) => {
                  if (node) linkHandleNodesRef.current[anchor] = node;
                  else delete linkHandleNodesRef.current[anchor];
                }}
                name='linkEditorUi'
                x={margin + x}
                y={margin + y}
                radius={LINK_HANDLE_RADIUS / scale}
                fill='white'
                stroke={SUB_SHAPE_COLOR}
                strokeWidth={1.5 / scale}
                onPointerDown={startLinkDraft({ mode: 'create', fromId: activeSubShape.id, fromAnchor: anchor })}
                onMouseEnter={setStageCursor('crosshair')}
                onMouseLeave={setStageCursor('')}
              />
            ))}
            {/* Extrémités du lien sélectionné : on les tire vers une autre
                ancre, de la même forme ou d'une autre. */}
            {selectedLinkEnds && ['from', 'to'].map((end) => (
              <Circle
                key={end}
                ref={(node) => {
                  if (node) linkEndpointNodesRef.current[end] = node;
                  else delete linkEndpointNodesRef.current[end];
                }}
                name='linkEditorUi'
                x={margin + selectedLinkEnds[end][0]}
                y={margin + selectedLinkEnds[end][1]}
                radius={LINK_HANDLE_RADIUS / scale}
                fill={SUB_SHAPE_COLOR}
                stroke='white'
                strokeWidth={1.5 / scale}
                visible={!linkDraft}
                onPointerDown={startLinkDraft({ mode: 'retarget', end })}
                onMouseEnter={setStageCursor('move')}
                onMouseLeave={setStageCursor('')}
              />
            ))}
            {/* Ancres de la forme survolée pendant un tracé ; la plus proche,
                où le lien s'accrochera, est pleine. */}
            {hoveredSubShape && getAnchors(hoveredSubShape).map(({ anchor, point }) => (
              <Circle
                key={anchor}
                name='linkEditorUi'
                x={margin + point[0]}
                y={margin + point[1]}
                radius={(anchor === linkHover.anchor ? HOVER_ANCHOR_RADIUS + 1.5 : HOVER_ANCHOR_RADIUS) / scale}
                fill={anchor === linkHover.anchor ? SUB_SHAPE_COLOR : 'white'}
                stroke={SUB_SHAPE_COLOR}
                strokeWidth={1.5 / scale}
                listening={false}
              />
            ))}
            {linkDraft && (
              <Line
                ref={draftLineRef}
                name='linkEditorUi'
                stroke={SUB_SHAPE_COLOR}
                strokeWidth={2 / scale}
                dash={[6 / scale, 4 / scale]}
                listening={false}
              />
            )}
            {/* Toujours montés, affichés à la demande (cf. updateSnapGuides). */}
            <Line ref={guideXRef} visible={false} strokeWidth={1 / scale} dash={[4 / scale, 4 / scale]} listening={false} />
            <Line ref={guideYRef} visible={false} strokeWidth={1 / scale} dash={[4 / scale, 4 / scale]} listening={false} />
          </Layer>
        </Stage>
        {/* Textes, dans le repère du Layer (décalage + zoom). */}
        <div
          onKeyDownCapture={stopTextEditingOnEscape}
          style={{
            position: 'absolute',
            left: layerOffsetX,
            top: layerOffsetY,
            transform: `scale(${scale})`,
            transformOrigin: '0 0',
            pointerEvents: 'none',
          }}
        >
          <ShapeTextOverlay box={box} margin={margin} editing={textEditing} registerNode={registerTextNode} />
        </div>
      </div>
      {/* display/flexDirection/gap : contrairement au panneau de droite
          (BoxFormControlsPortal, qui espace déjà ses lignes), ce panneau de
          modale rend ShapeComponent à plat, sans conteneur qui espace ses
          lignes entre elles — sans quoi elles se touchent. */}
      <div style={{ width: 300, overflowY: 'auto', paddingRight: 4, display: 'flex', flexDirection: 'column', gap: 4 }}>
        {sidePanelExtra}
        {/* Mise en forme du texte en cours de saisie (cf. textEditing) : l'hôte
            reste monté section repliée, les réglages y sont téléportés. */}
        {textEditing && (
          <ShapePanelSections
            groupKey='shape-text-format'
            defaultOpenKeys={['format']}
            sections={[{
              key: 'format',
              title: t('shape-text-format'),
              forceRender: true,
              children: <div ref={setTextPanelHost} style={{ display: 'flex', flexDirection: 'column' }} />,
            }]}
          />
        )}
        {/* Largeur/hauteur libres du conteneur : rendues par ShapeComponent
            lui-même (cf. Shape.jsx) — le même panneau qu'en édition en
            place, pas un doublon propre à la modale. */}
        {/* Plusieurs formes sélectionnées : tout le panneau est désactivé (`inert`
            couvre boutons, champs et sélecteurs, natifs ou non) — on ne peut
            que les déplacer. */}
        {isLinkSelected && (
          <ShapeLinkPanel
            box={box}
            ownerId={selectedLink.ownerId}
            linkId={selectedLink.linkId}
            onEditText={() => handleEditLinkText(selectedLink.ownerId, selectedLink.linkId)}
          />
        )}
        <div
          inert={isMultiSelection}
          style={{ ...(isMultiSelection ? { opacity: 0.5 } : {}), display: isLinkSelected ? 'none' : undefined }}
        >
          <ShapeComponent
            selectedBox={box}
            isCompound
            subShapes={subShapes}
            activeSubShapeId={activeSubShapeId}
            onSelectSubShapeId={handleSelectSubShapeFromPanel}
            onEditSubShapeText={handleEditSubShapeText}
            onGenerateThumbnail={handleGenerateThumbnail}
            hideLibrarySave={fullscreen}
            richEditing
          />
        </div>
      </div>
    </div>
    </FormControlsPortalContext.Provider>
  );

  if (fullscreen) {
    return (
      <div style={{ width: '100vw', height: '100vh', padding: 16, boxSizing: 'border-box', background: '#fff' }}>
        {content}
      </div>
    );
  }

  return (
    <Modal
      title={t('shape-fullscreen-editor-title')}
      open={open}
      onCancel={onClose}
      footer={null}
      width='90vw'
      centered
      // Le contenu de la modale vit dans un portail, mais reste un enfant
      // React de la box : sans ce marqueur, cliquer dedans (le damier, une
      // sous-forme, un champ du panneau...) est vu comme un clic hors de la
      // box et la désélectionne — ce qui démonte la modale elle-même, sans
      // même passer par onCancel. Même remède que SectionPreviewModal.jsx.
      modalRender={(node) => <div data-outside-ignore="true">{node}</div>}
      destroyOnClose
    >
      {content}
    </Modal>
  );
});

export default ShapeFullscreenEditor;
